/**
 * MSW handlers — let the app run standalone against synthetic data with no
 * live M/P services. Handlers match the same URLs the RTK Query clients call,
 * built from VITE_OXBO_M_URL / VITE_OXBO_P_URL, and return the SAME wire shapes
 * (snake_case WellOut / EventOut / DailyReportOut / pivots) as the real
 * services — so flipping VITE_USE_MOCKS=false is a true drop-in swap.
 */
import { http, HttpResponse } from "msw";
import type {
  WellOut,
  DailyReportOut,
  DailyReportCreate,
  DailyReportUpdate,
  InboxResponse,
  CostPivotResponse,
  NptRollupResponse,
  PlanVsActualResponse,
  AfeLineGridResponse,
  ReportCloseResponse,
  CategoryOut,
  EntryOut,
  EntryCreate,
  EntryUpdate,
  WellboreOut,
  WellboreCreate,
  WellborePatch,
} from "@api/types";
import {
  WELLS,
  WELL_BY_ID,
  WELLBORES_BY_WELL,
  EVENTS_BY_WELL_UWI,
  EVENTS,
  ACTIVITIES_BY_EVENT,
  REPORTS_BY_ID,
  REPORTS_BY_EVENT,
  COST_PIVOTS,
  PLAN_VS_ACTUAL,
  AFE_LINES,
  NPT_ROLLUPS,
  INBOX,
  CATALOGS,
} from "./seed";

const M = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";
const P = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

// A mutable copy so create/patch/close mutations persist within a session.
const reports: Record<string, DailyReportOut> = { ...REPORTS_BY_ID };
const reportsByEvent: Record<string, DailyReportOut[]> = Object.fromEntries(
  Object.entries(REPORTS_BY_EVENT).map(([k, v]) => [k, [...v]]),
);

// Mutable per-category entry store for the Picklist Admin, seeded from the
// resolved CATALOGS so create/update/delete persist within a dev session.
const catalogEntries: Record<string, EntryOut[]> = Object.fromEntries(
  Object.entries(CATALOGS).map(([key, cat]) => [key, cat.entries.map((e) => ({ ...e }))]),
);

// Mutable per-well wellbore store (OH + sidetracks), seeded from WELLBORES_BY_WELL
// so create/edit persist within a dev session. A well not in the seed starts with
// an implicit empty list.
const wellboreStores: Record<string, WellboreOut[]> = {};
function wellboreStore(wellId: string): WellboreOut[] {
  if (!wellboreStores[wellId]) {
    wellboreStores[wellId] = (WELLBORES_BY_WELL[wellId] ?? []).map((w) => ({ ...w }));
  }
  return wellboreStores[wellId];
}

/** Synthetic CategoryOut headers for the categories present in the mock. */
function mockCategories(): CategoryOut[] {
  const NOW = new Date().toISOString();
  return Object.keys(catalogEntries).map((key) => ({
    category_key: key,
    display_name: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    category_group: key.startsWith("activity") ? "Operations" : "Cost & Catalog",
    property_schema: null,
    override_levels: ["org"],
    retired_at: null,
    created_at: NOW,
    created_by: null,
    modified_at: NOW,
    modified_by: null,
  }));
}

function filterWells(url: URL): WellOut[] {
  const q = url.searchParams;
  const status = q.get("status") ?? "";
  const activeOnly = q.get("active_only") === "true";
  return WELLS.filter((w) => {
    if (status && w.status !== status) return false;
    if (activeOnly && !w.is_active) return false;
    return true;
  });
}

export const handlers = [
  // ─────────────────────────── Oxbo-M ───────────────────────────
  http.get(`${M}/v1/wells`, ({ request }) => {
    return HttpResponse.json(filterWells(new URL(request.url)));
  }),
  http.get(`${M}/v1/wells/:id`, ({ params }) => {
    const well = WELL_BY_ID[params.id as string];
    return well ? HttpResponse.json(well) : new HttpResponse(null, { status: 404 });
  }),

  // ── Wellbores (OH + sidetracks) — in-memory store over the seed ──
  http.get(`${M}/v1/wells/:id/wellbores`, ({ params }) => {
    const wellId = params.id as string;
    if (!WELL_BY_ID[wellId]) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(wellboreStore(wellId));
  }),
  http.post(`${M}/v1/wells/:id/wellbores`, async ({ params, request }) => {
    const wellId = params.id as string;
    if (!WELL_BY_ID[wellId]) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as WellboreCreate;
    const store = wellboreStore(wellId);
    if (body.is_sidetrack && !body.parent_wellbore_id) {
      return HttpResponse.json({ detail: "A sidetrack requires parent_wellbore_id." }, { status: 422 });
    }
    const n = store.filter((w) => w.is_sidetrack).length + 1;
    const no = body.wellbore_no ?? (body.is_sidetrack ? `ST${n}` : "OH");
    const created: WellboreOut = {
      id: `wb-${wellId}-${Date.now()}`,
      entity_id: `ent-${wellId}-${Date.now()}`,
      organization_id: WELL_BY_ID[wellId].organization_id,
      well_id: wellId,
      wellbore_key: no,
      wellbore_no: no,
      wellbore_name: body.wellbore_name ?? null,
      wellbore_type: body.is_sidetrack ? "unplanned_sidetrack" : "planned",
      is_sidetrack: body.is_sidetrack,
      parent_wellbore_id: body.parent_wellbore_id ?? null,
      ko_date: body.ko_date ?? null,
      ko_md: body.ko_md ?? null,
      ko_tvd: body.ko_tvd ?? null,
      authorized_md: body.authorized_md ?? null,
      authorized_tvd: body.authorized_tvd ?? null,
      reason: body.reason ?? null,
      operational_status: null,
      version: 1,
      status: "active",
    };
    store.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${M}/v1/wells/:id/wellbores/:wbId`, async ({ params, request }) => {
    const wellId = params.id as string;
    const store = wellboreStore(wellId);
    const idx = store.findIndex((w) => w.id === params.wbId);
    const cur = idx === -1 ? undefined : store[idx];
    if (!cur) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as WellborePatch;
    const next: WellboreOut = {
      ...cur,
      version: cur.version + 1,
      wellbore_name: patch.wellbore_name ?? cur.wellbore_name,
      wellbore_no: patch.wellbore_no ?? cur.wellbore_no,
      is_sidetrack: patch.is_sidetrack ?? cur.is_sidetrack,
      wellbore_type:
        patch.is_sidetrack == null
          ? cur.wellbore_type
          : patch.is_sidetrack
            ? "unplanned_sidetrack"
            : "planned",
      parent_wellbore_id:
        patch.parent_wellbore_id !== undefined ? patch.parent_wellbore_id : cur.parent_wellbore_id,
      ko_date: patch.ko_date !== undefined ? patch.ko_date : cur.ko_date,
      ko_md: patch.ko_md !== undefined ? patch.ko_md : cur.ko_md,
      ko_tvd: patch.ko_tvd !== undefined ? patch.ko_tvd : cur.ko_tvd,
      authorized_md: patch.authorized_md !== undefined ? patch.authorized_md : cur.authorized_md,
      authorized_tvd: patch.authorized_tvd !== undefined ? patch.authorized_tvd : cur.authorized_tvd,
      reason: patch.reason !== undefined ? patch.reason : cur.reason,
    };
    store[idx] = next;
    return HttpResponse.json(next);
  }),
  http.get(`${M}/v1/categories/:key/resolved`, ({ params }) => {
    const cat = CATALOGS[params.key as string];
    return cat
      ? HttpResponse.json(cat)
      : HttpResponse.json({ category_key: params.key, generation: "gen-0", entries: [] });
  }),

  // ── Picklist Admin (categories + entries CRUD) ──
  http.get(`${M}/v1/categories`, () => HttpResponse.json(mockCategories())),

  http.get(`${M}/v1/categories/:key/entries`, ({ params }) => {
    return HttpResponse.json(catalogEntries[params.key as string] ?? []);
  }),

  http.post(`${M}/v1/categories/:key/entries`, async ({ params, request }) => {
    const key = params.key as string;
    const body = (await request.json()) as EntryCreate;
    const now = new Date().toISOString();
    const created: EntryOut = {
      id: `${key}:${body.entry_key}:${Date.now()}`,
      category_key: key,
      organization_id: body.admin_org_id ?? null,
      site_region_key: null,
      entry_key: body.entry_key,
      display_label: body.display_label,
      aliases: body.aliases ?? null,
      properties: body.properties ?? null,
      is_active: body.is_active ?? true,
      superseded_by: null,
      valid_from: null,
      valid_to: null,
      created_at: now,
      created_by: "admin",
      modified_at: now,
      modified_by: "admin",
    };
    (catalogEntries[key] ??= []).push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.put(`${M}/v1/categories/:key/entries/:id`, async ({ params, request }) => {
    const key = params.key as string;
    const id = params.id as string;
    const list = catalogEntries[key] ?? [];
    const idx = list.findIndex((e) => e.id === id);
    const existing = list[idx];
    if (!existing) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as EntryUpdate;
    const updated: EntryOut = {
      ...existing,
      ...(patch.display_label !== undefined ? { display_label: patch.display_label } : {}),
      ...(patch.is_active !== undefined ? { is_active: patch.is_active } : {}),
      ...(patch.properties !== undefined ? { properties: patch.properties } : {}),
      ...(patch.aliases !== undefined ? { aliases: patch.aliases } : {}),
      modified_at: new Date().toISOString(),
    };
    list[idx] = updated;
    return HttpResponse.json(updated);
  }),

  http.delete(`${M}/v1/categories/:key/entries/:id`, ({ params }) => {
    const key = params.key as string;
    const id = params.id as string;
    const list = catalogEntries[key] ?? [];
    const idx = list.findIndex((e) => e.id === id);
    if (idx < 0) return new HttpResponse(null, { status: 404 });
    list.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // ─────────────────────────── Oxbo-P ───────────────────────────
  http.get(`${P}/v1/events`, () => {
    // Bulk tenant-wide feed for the Well Explorer.
    return HttpResponse.json(EVENTS);
  }),

  http.get(`${P}/v1/wells/:uwi/events`, ({ params }) => {
    const uwi = decodeURIComponent(params.uwi as string);
    return HttpResponse.json(EVENTS_BY_WELL_UWI[uwi] ?? []);
  }),

  http.get(`${P}/v1/events/:eventId/activities`, ({ params }) => {
    return HttpResponse.json(ACTIVITIES_BY_EVENT[params.eventId as string] ?? []);
  }),

  http.get(`${P}/v1/events/:eventId/reports`, ({ params }) => {
    const list = reportsByEvent[params.eventId as string] ?? [];
    // Newest report first, matching a typical server ordering.
    return HttpResponse.json([...list].sort((a, b) => b.report_no - a.report_no));
  }),

  http.get(`${P}/v1/reports/:id`, ({ params }) => {
    const r = reports[params.id as string];
    return r ? HttpResponse.json(r) : new HttpResponse(null, { status: 404 });
  }),

  http.post(`${P}/v1/reports`, async ({ request }) => {
    const body = (await request.json()) as DailyReportCreate;
    const id = `ddr-${body.event_id}-${body.report_no}`;
    const created: DailyReportOut = {
      event_id: body.event_id,
      well_uwi: body.well_uwi,
      report_no: body.report_no,
      date_report: body.date_report,
      comment_summary: body.comment_summary ?? null,
      comment_forecast_24: body.comment_forecast_24 ?? null,
      supervisor_1_entry_key: body.supervisor_1_entry_key ?? null,
      md_current: body.md_current ?? null,
      tvd_current: body.tvd_current ?? null,
      id,
      state: "open",
      is_last_of_event: true,
      // Server would compute these; mock returns plausible derived values.
      days_total: body.report_no,
      days_op: body.report_no,
      pct_complete: null,
      rop_avg_day: null,
      daily_cost: null,
    };
    reports[id] = created;
    (reportsByEvent[body.event_id] ??= []).push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.patch(`${P}/v1/reports/:id`, async ({ params, request }) => {
    const id = params.id as string;
    const existing = reports[id];
    if (!existing) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as DailyReportUpdate;
    const updated: DailyReportOut = { ...existing, ...patch };
    reports[id] = updated;
    const list = reportsByEvent[existing.event_id];
    if (list) {
      const idx = list.findIndex((r) => r.id === id);
      if (idx >= 0) list[idx] = updated;
    }
    return HttpResponse.json(updated);
  }),

  http.post(`${P}/v1/reports/:id/close`, ({ params }) => {
    const id = params.id as string;
    const existing = reports[id];
    if (!existing) return new HttpResponse(null, { status: 404 });
    reports[id] = { ...existing, state: "closed" };
    const resp: ReportCloseResponse = { id, state: "closed", warnings: [], closed: true };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/cost-pivot`, ({ params, request }) => {
    const dim = new URL(request.url).searchParams.get("dimension") ?? "vendor";
    const set = COST_PIVOTS[params.eventId as string];
    const rows = set?.[dim] ?? [];
    const total = rows.reduce((s, r) => s + r.total, 0);
    const resp: CostPivotResponse = { dimension: dim, rows, total };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/plan-vs-actual`, ({ params }) => {
    const pv = PLAN_VS_ACTUAL[params.eventId as string];
    const by_phase = pv?.by_phase ?? [];
    const afe_total = by_phase.reduce((s, p) => s + p.afe, 0);
    const actual_total = by_phase.reduce((s, p) => s + p.actual, 0);
    const resp: PlanVsActualResponse = {
      afe_total,
      actual_total,
      variance: actual_total - afe_total,
      by_phase,
    };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/afe-lines`, ({ params }) => {
    const lines = AFE_LINES[params.eventId as string] ?? [];
    const sum = (pick: (l: (typeof lines)[number]) => number | null) =>
      lines.reduce((s, l) => s + (pick(l) ?? 0), 0);
    const afe_total = sum((l) => l.afe_amount);
    const accrued_total = sum((l) => l.accrued);
    const actual_total = sum((l) => l.actual);
    const invoiced_total = sum((l) => l.invoiced);
    const currencies = new Set(lines.map((l) => l.currency).filter(Boolean));
    const resp: AfeLineGridResponse = {
      event_id: params.eventId as string,
      lines,
      afe_total,
      accrued_total,
      actual_total,
      invoiced_total,
      variance: actual_total - afe_total,
      currency: currencies.size === 1 ? [...currencies][0]! : currencies.size ? "MIXED" : null,
    };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/events/:eventId/npt-rollup`, ({ params }) => {
    const n = NPT_ROLLUPS[params.eventId as string];
    if (!n) {
      const empty: NptRollupResponse = {
        total_hours: 0,
        npt_hours: 0,
        npt_pct: null,
        by_class: [],
      };
      return HttpResponse.json(empty);
    }
    const resp: NptRollupResponse = {
      total_hours: n.total_hours,
      npt_hours: n.npt_hours,
      npt_pct: n.total_hours ? (n.npt_hours / n.total_hours) * 100 : null,
      by_class: n.by_class,
    };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/inbox`, ({ request }) => {
    const q = new URL(request.url).searchParams;
    const wellUwi = q.get("well_uwi") ?? "";
    const state = q.get("state") ?? "";
    const items = INBOX.filter((i) => {
      if (wellUwi && i.well_uwi !== wellUwi) return false;
      if (state && i.state !== state) return false;
      return true;
    });
    const resp: InboxResponse = { items, next_cursor: null };
    return HttpResponse.json(resp);
  }),

  http.get(`${P}/v1/saved-queries`, () => HttpResponse.json([])),
];
